import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../api'

// ─── Types ───────────────────────────────────────────────────────────────────

export type QuestionType = 'multiple_choice' | 'true_false'

/** Opção como o ADMIN vê: inclui o gabarito, necessário para editar o quiz. */
export interface QuizOption {
  id: number
  questionId: number
  text: string
  isCorrect: boolean
  order: number
}

/**
 * Opção como o ALUNO vê: sem `isCorrect`.
 *
 * O backend deixou de enviar o gabarito junto com as perguntas — ele
 * entregava a resposta certa antes de o aluno responder. O veredito chega
 * na resposta de `POST /quizzes/:id/answer`.
 */
export type StudentQuizOption = Omit<QuizOption, 'isCorrect'>

export interface QuizQuestion {
  id: number
  quizId: number
  text: string
  type: QuestionType
  order: number
  explanation?: string
  options: QuizOption[]
}

export interface Quiz {
  id: number
  episodeId: number
  title: string
  description?: string
  status: 'draft' | 'published'
  questions: QuizQuestion[]
  createdAt: string
  updatedAt: string
  /**
   * Bag opcional com contexto hierárquico (episode → module → course)
   * que o endpoint `GET /admin/quizzes` já devolve. Front usa pra
   * cascading select no `BadgeFormDialog` (curso → módulo → quiz)
   * sem precisar de query extra.
   */
  episodes?: {
    id: number
    title: string
    moduleId: number
    modules?: {
      id: number
      title: string
      courseId: number
    }
  }
}

export interface CreateQuizDTO {
  episodeId: number
  title: string
  description?: string
}

export interface UpdateQuizDTO {
  title?: string
  description?: string
}

export interface CreateQuestionDTO {
  quizId: number
  text: string
  type: QuestionType
  order?: number
  explanation?: string
  options: { text: string; isCorrect: boolean; order?: number }[]
}

export interface UpdateQuestionDTO {
  text?: string
  type?: QuestionType
  order?: number
  explanation?: string
  options?: { id?: number; text: string; isCorrect: boolean; order?: number }[]
}

/**
 * O aluno envia só o que escolheu — nota e veredito são calculados no
 * servidor, a partir de `quiz_options`.
 */
export interface SubmitQuizAttemptDTO {
  answers: { questionId: number; selectedOptionId: number; timeSpent?: number }[]
}

export interface SaveAnswerDTO {
  questionId: number
  selectedOptionId: number
  timeSpent?: number
}

/** Correção devolvida pelo servidor ao registrar uma resposta. */
export interface AnswerVerdict {
  questionId: number
  selectedOptionId: number
  isCorrect: boolean
  correctOptionId: number | null
  explanation: string | null
}

export interface QuizAttempt {
  id: string
  userId: string
  quizId: number
  attemptNumber: number
  status: 'in_progress' | 'completed'
  score: number
  correctAnswers: number
  totalQuestions: number
  answers: {
    questionId: number
    selectedOptionId: number
    isCorrect: boolean
    timeSpent?: number
  }[]
  completedAt: string | null
  createdAt: string
}

// ─── Quiz Hooks (API real) ──────────────────────────────────────────────────

export function useAdminQuizzes() {
  return useQuery({
    queryKey: ['admin-quizzes'],
    queryFn: async () => {
      const response = await api.get<Quiz[]>('/admin/quizzes')
      return response.data
    },
  })
}

export function useAdminQuizByEpisode(episodeId: number | undefined) {
  return useQuery({
    queryKey: ['admin-quiz-episode', episodeId],
    queryFn: async () => {
      const response = await api.get<Quiz | null>(`/admin/quizzes/episode/${episodeId}`)
      return response.data
    },
    enabled: !!episodeId,
  })
}

export function useCreateQuiz() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: CreateQuizDTO) => {
      const response = await api.post<Quiz>('/admin/quizzes', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-quizzes'] })
      queryClient.invalidateQueries({ queryKey: ['admin-quiz-episode'] })
    },
  })
}

export function useUpdateQuiz() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateQuizDTO }) => {
      const response = await api.put<Quiz>(`/admin/quizzes/${id}`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-quizzes'] })
      queryClient.invalidateQueries({ queryKey: ['admin-quiz-episode'] })
    },
  })
}

export function useUpdateQuizStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, status }: { id: number; status: 'draft' | 'published' }) => {
      const response = await api.patch<Quiz>(`/admin/quizzes/${id}/status`, { status })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-quizzes'] })
      queryClient.invalidateQueries({ queryKey: ['admin-quiz-episode'] })
      queryClient.invalidateQueries({ queryKey: ['quiz-episode'] })
    },
  })
}

export function useDeleteQuiz() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/admin/quizzes/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-quizzes'] })
      queryClient.invalidateQueries({ queryKey: ['admin-quiz-episode'] })
    },
  })
}

// ─── Public hook (alunos) ───────────────────────────────────────────────────

export function useQuizByEpisode(episodeId: number | undefined) {
  return useQuery({
    queryKey: ['quiz-episode', episodeId],
    queryFn: async () => {
      const response = await api.get<Quiz | null>(`/quizzes/episode/${episodeId}`)
      return response.data
    },
    enabled: !!episodeId,
  })
}

export function useQuizAttempt(quizId: number | undefined) {
  return useQuery({
    queryKey: ['quiz-attempt', quizId],
    queryFn: async () => {
      const response = await api.get<QuizAttempt | null>(`/quizzes/${quizId}/attempt`)
      return response.data
    },
    enabled: !!quizId,
  })
}

export function useSaveAnswer() {
  return useMutation({
    mutationFn: async ({ quizId, answer }: { quizId: number; answer: SaveAnswerDTO }) => {
      // A resposta traz `verdict`: é dele que a tela monta o feedback, já que
      // o gabarito não vem mais junto com as perguntas.
      const response = await api.post<QuizAttempt & { verdict: AnswerVerdict }>(
        `/quizzes/${quizId}/answer`,
        answer
      )
      return response.data
    },
  })
}

export function useSubmitQuizAttempt() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ quizId, data }: { quizId: number; data: SubmitQuizAttemptDTO }) => {
      const response = await api.post<QuizAttempt>(`/quizzes/${quizId}/submit`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quiz-episode'] })
      queryClient.invalidateQueries({ queryKey: ['quiz-attempt'] })
      queryClient.invalidateQueries({ queryKey: ['episode'] })
    },
  })
}

// ─── Question Hooks (API real) ──────────────────────────────────────────────

export function useCreateQuestion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: CreateQuestionDTO) => {
      const response = await api.post<QuizQuestion>('/admin/quizzes/questions', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-quizzes'] })
      queryClient.invalidateQueries({ queryKey: ['admin-quiz-episode'] })
    },
  })
}

export function useUpdateQuestion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateQuestionDTO }) => {
      const response = await api.put<QuizQuestion>(`/admin/quizzes/questions/${id}`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-quizzes'] })
      queryClient.invalidateQueries({ queryKey: ['admin-quiz-episode'] })
    },
  })
}

export function useDeleteQuestion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/admin/quizzes/questions/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-quizzes'] })
      queryClient.invalidateQueries({ queryKey: ['admin-quiz-episode'] })
    },
  })
}
